/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { GlobalConstants } from '../../../feature/src/lib/constants/global-constants';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { SysqueElectricalState } from '../../../../toolbelt/feature/src/lib/models/sysque-electrical-state';
import { Item } from '../../../data-access/src/lib/domain/item';
import { SystemInfo } from '../../../data-access/src/lib/domain/system-info';
import { TabDescription } from '../../../data-access/src/lib/domain/tab-description';
import { SysQueStateService } from './sysque-electrical-state/sysque-elecrical-state.service';
import { LocalStorageService } from './local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class SystemStateManager {
  stateChangeTrigger: BehaviorSubject<string> = new BehaviorSubject<string>('');
  dockedModeState!: SysqueElectricalState;
  addFittingModeState!: SysqueElectricalState;
  manageModeState!: SysqueElectricalState;
  manageModeDestinationState: SysqueElectricalState = new SysqueElectricalState();
  manageModeSourceState: SysqueElectricalState = new SysqueElectricalState();

  constructor(private sysqueStateService: SysQueStateService, private localStorageService: LocalStorageService) {}

  public saveChanges(workflowKey: string): void {
    switch (workflowKey) {
      case GlobalConstants.Docked2Fitting:
        this.fromDockedModeToAddFittingMode();
        break;

      case GlobalConstants.Fitting2Docked:
        this.fromAddFittingModeToDockedMode();
        break;

      case GlobalConstants.Fitting2Manage:
        this.fromAddFittingModeToManageMode();
        break;

      case GlobalConstants.Manage2Fitting:
        this.fromManageModeToAddFittingMode();
        break;
      default:
        return;
    }
  }

  public fromDockedModeToAddFittingMode(): void {
    const state = localStorage.getItem(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY) ?? null;
    this.dockedModeState = state && JSON.parse(state);
    this.addFittingModeState = this.dockedModeState;
    this.sysqueStateService.setElectricalState(this.addFittingModeState);
  }

  public fromAddFittingModeToDockedMode(): void {
    const state = this.sysqueStateService.getElectricalState();
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.AddFittingsKey, false);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UpdateSystemInfoOnDockableFromAddFittings, true);
  }

  public fromAddFittingModeToManageMode(): void {
    this.addFittingModeState = this.sysqueStateService.getElectricalState();
    this.sysqueStateService.setManageSrcState(this.addFittingModeState);
    this.sysqueStateService.setManageDestState(this.addFittingModeState);
  }

  public fromManageModeToAddFittingMode(): void {
    const state = this.sysqueStateService.getManageSrcState();
    this.addFittingModeState = state;
    this.sysqueStateService.setElectricalState(state);
  }

  public resetFittingMode(): void {
    this.sysqueStateService.setElectricalState(new SysqueElectricalState());
    localStorage.setItem(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, JSON.stringify(new SysqueElectricalState()));
    this.resetManageMode();
  }

  public resetManageMode(): void {
    this.sysqueStateService.setManageSrcState(new SysqueElectricalState());
    this.sysqueStateService.setManageDestState(new SysqueElectricalState());
  }

  public getDockedModeState(): SysqueElectricalState {
    const state = localStorage.getItem(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY) ?? null;
    this.dockedModeState = state && JSON.parse(state);
    return this.dockedModeState;
  }

  public getAddFittingModeState(): SysqueElectricalState {
    this.addFittingModeState = this.sysqueStateService.getElectricalState();
    return this.addFittingModeState;
  }

  public getManageModeDestinationState(): SysqueElectricalState {
    this.manageModeDestinationState = this.sysqueStateService.getManageDestState();
    return this.manageModeDestinationState;
  }

  public getManageModeSourceState(): SysqueElectricalState {
    this.manageModeSourceState = this.sysqueStateService.getManageSrcState();
    return this.manageModeSourceState;
  }

  public selectFittingForSource(): Observable<Item[]> {
    this.manageModeSourceState = this.sysqueStateService.getManageSrcState();
    return of(this.manageModeSourceState?.fittingList);
  }

  public selectFittingForDestination(): Observable<Item[]> {
    this.manageModeDestinationState = this.sysqueStateService.getManageDestState();
    return of(this.manageModeDestinationState?.fittingList);
  }

  public selectSystemsForSource(): Observable<SystemInfo[]> {
    this.manageModeSourceState = this.sysqueStateService.getManageSrcState();
    return of(this.manageModeSourceState?.systemList);
  }

  public selectSystemsForDestination(): Observable<SystemInfo[]> {
    this.manageModeDestinationState = this.sysqueStateService.getManageDestState();
    return of(this.manageModeDestinationState?.systemList);
  }

  public selectCategoriesForDestination(): Observable<TabDescription[]> {
    this.manageModeDestinationState = this.sysqueStateService.getManageDestState();
    return of(this.manageModeDestinationState?.categoryList);
  }

  public selectCategoriesForSource(): Observable<TabDescription[]> {
    this.manageModeSourceState = this.sysqueStateService.getManageSrcState();
    return of(this.manageModeSourceState?.categoryList);
  }

  public setDockedState(state: SysqueElectricalState): void {
    localStorage.setItem(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, JSON.stringify(state));
  }

  public setAddFittingState(addFittingState: SysqueElectricalState): void {
    this.sysqueStateService.setElectricalState(addFittingState);
  }

  public setManageModeSourceState(manageModeState: SysqueElectricalState): void {
    this.sysqueStateService.setManageSrcState(manageModeState);
  }

  public setManageModeDestinationState(manageModeState: SysqueElectricalState): void {
    this.sysqueStateService.setManageDestState(manageModeState);
  }
}